import { memo, useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import { PhysicsData, ChemistryData } from "../../../../models/coreModels";
import { NotesFieldDE } from "../../../../../../shared/data-entries/common/NotesFieldDE";

// Fixed elements to display in chemistry table
const CHEMISTRY_ELEMENTS = [
  "C",
  "Mn",
  "S",
  "P",
  "Si",
  "Cr",
  "Ni",
  "Mo",
  "Cu",
  "V",
  "Sn",
  "Nb",
  "N",
];

// Process chemistry data: pivot into rows by Sample
const processChemistryData = (chemistryData: ChemistryData[]) => {
  if (!chemistryData?.length) {
    return [];
  }

  // Pivot data: group by Sample, then map element values
  const sampleMap = new Map<string, Record<string, number | string>>();
  chemistryData.forEach((item) => {
    if (!sampleMap.has(item.Sample)) {
      sampleMap.set(item.Sample, { Sample: item.Sample });
    }
    const row = sampleMap.get(item.Sample)!;
    row[item.ElementName] = item.Value;
  });

  return Array.from(sampleMap.values());
};

// Style configurations for different variants
const styles = {
  compact: {
    headerCell: {
      fontWeight: "bold",
      padding: "4px 2px",
      fontSize: "12px",
      whiteSpace: "nowrap",
      textAlign: "center" as const,
    },
    bodyCell: {
      padding: "4px 2px",
      fontSize: "12px",
      textAlign: "center" as const,
    },
    headerPadding: "8px 12px",
    headerFontSize: "13px",
    sampleCodeFontSize: "10px",
    containerMarginTop: 1.5,
  },
  full: {
    headerCell: {
      fontWeight: "bold",
      padding: "6px 4px",
      fontSize: "14px",
      whiteSpace: "nowrap",
      textAlign: "center" as const,
    },
    bodyCell: {
      padding: "6px 4px",
      fontSize: "14px",
      textAlign: "center" as const,
    },
    headerPadding: "6px",
    headerFontSize: "16px",
    sampleCodeFontSize: "12px",
    containerMarginTop: 0,
  },
};

interface HeatInfo {
  BilletsGroupID: number;
  HeatNumber: string;
  Grade: string;
  GroupName: string;
}

interface LabDataSectionProps {
  heat: HeatInfo | null;
  physicsData?: PhysicsData[];
  chemistryData?: ChemistryData[];
  variant?: "compact" | "full";
  isLoading?: boolean;
  comment?: string | null;
  onSaveComment?: (
    heatNumber: string,
    comment: string,
    billetsGroupId: number,
  ) => void | Promise<void>;
}

export const LabDataSection = memo(
  ({
    heat,
    physicsData = [],
    chemistryData = [],
    variant = "compact",
    isLoading = false,
    comment = null,
    onSaveComment,
  }: LabDataSectionProps) => {
    const [commentText, setCommentText] = useState("");
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      setCommentText(comment ?? "");
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [heat?.BilletsGroupID]);

    useEffect(() => {
      return () => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      };
    }, []);

    const handleCommentChange = useCallback(
      (value: string) => {
        setCommentText(value);
        if (!onSaveComment || !heat) return;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
          onSaveComment(heat.HeatNumber, value, heat.BilletsGroupID);
        }, 10000);
      },
      [onSaveComment, heat],
    );

    const flushSaveComment = useCallback(() => {
      if (!onSaveComment || !heat) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      const normalized = commentText.trim() === "" ? "" : commentText;
      if (normalized !== commentText) {
        setCommentText(normalized);
      }
      if (normalized !== (comment ?? "")) {
        onSaveComment(heat.HeatNumber, normalized, heat.BilletsGroupID);
      }
    }, [onSaveComment, heat, commentText, comment]);

    // Process chemistry data to get pivoted rows
    const chemistryRows = useMemo(
      () => processChemistryData(chemistryData),
      [chemistryData],
    );

    if (!heat) return null;

    const variantStyles = styles[variant];

    // Show loading spinner when fetching data
    if (isLoading) {
      return (
        <Box
          sx={{ marginTop: variantStyles.containerMarginTop, flexShrink: 0 }}
        >
          <Box
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              color: "white",
              padding: variantStyles.headerPadding,
              borderRadius: "4px 4px 0 0",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: variantStyles.headerFontSize,
                fontWeight: "bold",
                color: "white",
              }}
            >
              {heat.HeatNumber} {heat.Grade} {heat.GroupName}
            </Typography>
          </Box>
          <Paper
            elevation={0}
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderTop: "none",
              borderRadius: "0 0 4px 4px",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "150px",
            }}
          >
            <CircularProgress size={40} />
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ marginTop: "1rem" }}
            >
              Loading lab data...
            </Typography>
          </Paper>
        </Box>
      );
    }

    return (
      <Box sx={{ marginTop: variantStyles.containerMarginTop, flexShrink: 0 }}>
        {/* Heat Title Header */}
        <Box
          sx={{
            backgroundColor: (theme) => theme.palette.primary.main,
            color: "white",
            padding: variantStyles.headerPadding,
            borderRadius: "4px 4px 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: variantStyles.headerFontSize,
              fontWeight: "bold",
              color: "white",
            }}
          >
            {heat.HeatNumber} {heat.GroupName}
          </Typography>
        </Box>

        {/* Tables Container */}
        <Paper
          elevation={0}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderTop: "none",
            borderRadius: "0 0 4px 4px",
            padding: "8px",
          }}
        >
          {/* Chemistry Table - Dynamic columns based on elements */}
          <TableContainer sx={{ marginBottom: "8px" }}>
            <Table size="small" sx={{ tableLayout: "auto", width: "100%" }}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: (theme) => theme.palette.grey[200],
                  }}
                >
                  <TableCell sx={variantStyles.headerCell}>Sample</TableCell>
                  {CHEMISTRY_ELEMENTS.map((element) => (
                    <TableCell key={element} sx={variantStyles.headerCell}>
                      {element}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {chemistryRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={CHEMISTRY_ELEMENTS.length + 1}
                      sx={{ ...variantStyles.bodyCell, textAlign: "center" }}
                    >
                      No chemistry data available
                    </TableCell>
                  </TableRow>
                ) : (
                  chemistryRows.map(
                    (row: Record<string, string | number>, index: number) => (
                      <TableRow
                        key={row.Sample}
                        sx={{
                          backgroundColor: (theme) =>
                            index % 2 === 0
                              ? theme.palette.background.paper
                              : theme.palette.grey[50],
                        }}
                      >
                        <TableCell
                          sx={{ ...variantStyles.bodyCell, fontWeight: 600 }}
                        >
                          {row.Sample}
                        </TableCell>
                        {CHEMISTRY_ELEMENTS.map((element) => (
                          <TableCell key={element} sx={variantStyles.bodyCell}>
                            {row[element] ?? "-"}
                          </TableCell>
                        ))}
                      </TableRow>
                    ),
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Physics Table */}
          <TableContainer>
            <Table size="small" sx={{ tableLayout: "auto", width: "100%" }}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: (theme) => theme.palette.grey[200],
                  }}
                >
                  {[
                    "Section",
                    "Sample",
                    "Est. Yield",
                    "Est. Tensile",
                    "Est. Elong.",
                  ].map((header) => (
                    <TableCell key={header} sx={variantStyles.headerCell}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {physicsData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      sx={{ ...variantStyles.bodyCell, textAlign: "center" }}
                    >
                      No physics data available
                    </TableCell>
                  </TableRow>
                ) : (
                  physicsData.map((sample, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: (theme) =>
                          index % 2 === 0
                            ? theme.palette.background.paper
                            : theme.palette.grey[50],
                      }}
                    >
                      <TableCell sx={variantStyles.bodyCell}>
                        {sample.Section}
                      </TableCell>
                      <TableCell sx={variantStyles.bodyCell}>
                        {sample.Sample}{" "}
                        <Typography
                          component="span"
                          sx={{
                            fontSize: variantStyles.sampleCodeFontSize,
                            color: "text.secondary",
                          }}
                        >
                          ({sample.SectionCode})
                        </Typography>
                      </TableCell>
                      <TableCell sx={variantStyles.bodyCell}>
                        {sample.EstYield}
                      </TableCell>
                      <TableCell sx={variantStyles.bodyCell}>
                        {sample.EstTensile}
                      </TableCell>
                      <TableCell sx={variantStyles.bodyCell}>
                        {sample.EstElong}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {onSaveComment ? (
            <Box sx={{ marginTop: "12px" }}>
              <NotesFieldDE
                value={commentText}
                onChange={handleCommentChange}
                onBlur={flushSaveComment}
                placeholder="Notes"
              />
            </Box>
          ) : comment ? (
            <Box sx={{ marginTop: "12px" }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: (theme) => theme.palette.text.secondary,
                }}
              >
                Notes
              </Typography>
              <Box
                sx={{
                  marginTop: "4px",
                  padding: "10px 12px",
                  border: (theme) => `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: "4px",
                  backgroundColor: (theme) => theme.palette.grey[50],
                  whiteSpace: "pre-wrap",
                  fontSize: "0.9rem",
                }}
              >
                {comment}
              </Box>
            </Box>
          ) : null}
        </Paper>
      </Box>
    );
  },
);

LabDataSection.displayName = "LabDataSection";
